import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, IconButton, Backdrop,Badge } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { api } from '../../services/api';
import { Trip } from '../../types/trip';
import { TripDetail } from '../../types/tripDetail';
import TripDetailPanel from './midsections/tripDetails/TripDetailPanel';
import TripDetailPanelHeader from './TripDetailPanelHeader';
import CallbackRequestPanel from './midsections/callbackRequest/CallbackRequestPanel';
import ContactRequestPanel from './midsections/contactRequest/ContactRequestPanel';
// @ts-ignore - Ignore TypeScript error for NewTripForm import
import NewTripForm from '../trips/NewTripForm';
import PricingTable from './midsections/pricing/PricingTable';
import { TripFormPersistenceService } from '../../services/tripFormPersistence';
import Reports from './reports/Reports';
import FlashDeals from './flashdeals/FlashDeals';
import { resetReportsState } from '../../store/slices/reportsSlice';
import { resetLeadConversionReport } from '../../store/slices/leadConversionReportSlice';
import CashTransactions from '../cashTransactions/CashTransactions';
import {
  MainContentWrapper,
  StyledTabs,
  StyledTab,
  TabPanel,
  ServiceSection,
  DetailsSection
} from './styles/MainContent.styles';
import CashTransactionReport from '../cashTransactionReport/CashTransactionReport';
import TripNotifications from '../tripNotifications/TripNotifications';
import { VehicleScheduler } from '../carScheduler/VehicleScheduler';
import LeadConversionReport from '../leadConversionReport/LeadConversionReport';


export interface MainContentProps {
  openTrips: Trip[];
  activeTab: string;
  onTabChange: (newValue: string) => void;
  onCloseTab: (tripId: string) => void;
  onAddTrip?: (trip: any) => void; // Add this prop to handle adding trips
}

const MainContent: React.FC<MainContentProps> = ({ 
  openTrips,
  activeTab,
  onTabChange,
  onCloseTab,
  onAddTrip
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [tripDetail, setTripDetail] = useState<TripDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [lastFetchedTab, setLastFetchedTab] = useState<string>('');
  const [isNavigating, setIsNavigating] = useState(false);
  const [ongoingRequests, setOngoingRequests] = useState<Set<string>>(new Set());

  const notifications = useSelector((state:RootState) => state.tripNotifications.notifications || []);

  useEffect(() => {
    const fetchTripDetail = async () => {
      // Check if current trip is a FlashDeal
      const currentTrip = openTrips.find(trip => (trip.tripId || trip.trip_id) === activeTab);
      const isFlashDeal = currentTrip?.tripType === 'FLASH_DEALS';
      
      // Skip fetching for the special tabs (new, pricing, callback, contact, Reports, FlashDeals)
      if (!activeTab || 
        activeTab === 'new' || 
        activeTab === 'pricing' || 
        activeTab === 'callback' || 
        activeTab === 'contact' || 
        activeTab === 'Reports' || 
        activeTab === 'FlashDeals' || 
        isFlashDeal || 
        activeTab === 'CashManagement'|| 
        activeTab === 'CashReports' || 
        activeTab === 'TripNotifications' || 
        activeTab === 'Scheduler' || 
        activeTab === 'LeadsReport') {
        setLoading(false);
        setTripDetail(null);
        setLastFetchedTab('');
        setIsNavigating(false);
        return;
      }
      
      // Check if request is already ongoing
      if (ongoingRequests.has(activeTab)) {
        return;
      }
      
      // Skip if same tab to prevent unnecessary calls
      if (activeTab === lastFetchedTab && tripDetail) {
        setIsNavigating(false);
        return;
      }  
      setLoading(true);
      setError(null);
      
      // Mark request as ongoing
      setOngoingRequests(prev => new Set(prev).add(activeTab));
      
      try {
        const response = await api.getTripDetails(activeTab);
        const typedResponse = response as any;
        
        setTripDetail(typedResponse);
        setLastFetchedTab(activeTab);
      } catch (err) {
        console.error('MainContent: Error fetching trip details:', err);
        setError('Failed to fetch trip details');
        setLastFetchedTab('');
      } finally {
        setLoading(false);
        setIsNavigating(false);
        // Remove from ongoing requests
        setOngoingRequests(prev => {
          const newSet = new Set(prev);
          newSet.delete(activeTab);
          return newSet;
        });
      }
    };

    // Add a small delay only for normal tab switches to prevent rapid calls
    const timeoutId = setTimeout(fetchTripDetail, isNavigating ? 0 : 100);
    return () => clearTimeout(timeoutId);
  }, [activeTab, isNavigating]);

  const handleNewTripFormClose = () => {
    // Check if there's a successfully created trip
    const shouldShowTripDetails = TripFormPersistenceService.shouldShowTripDetails();
    const createdTripCode = TripFormPersistenceService.getCreatedTripCode();
        
    if (shouldShowTripDetails && createdTripCode) {
      // If trip was created, switch to trip details instead of closing
      onTabChange(createdTripCode);
    } else {
      // Clear persisted data only when explicitly closing without success
      TripFormPersistenceService.clearFormData();
      onCloseTab('new');
    }
  };
  const handleTripCreated = (createdTripData?: any) => {
    
    // Handle DIRECT NAVIGATION - close current tab and open new one
    if (createdTripData && createdTripData.directNavigation && createdTripData.tripCode) {
      
      // Set navigating state to prevent tab flickering
      setIsNavigating(true);
      
      // Close the new trip tab first
      onCloseTab('new');
      
      // Clear any persisted form data since we're navigating away
      TripFormPersistenceService.clearFormData();
      
      // Add the trip to openTrips first, then change to it
      if (onAddTrip) {
        const newTrip = {
          tripId: createdTripData.tripCode,
          trip_id: createdTripData.tripCode,
          tripCode: createdTripData.tripCode,
          tripType: 'TRIP_DETAILS',
          carType: '',
          carModel: '',
          operator: ''
        };
        onAddTrip(newTrip);
        
        // Immediately change to the new tab without delay
        onTabChange(createdTripData.tripCode);
      }
      return;
    }
    
    if (createdTripData && createdTripData.navigateToDetails && createdTripData.tripCode) {
      // This is a navigation request to trip details - update the current tab instead of closing
      setIsNavigating(true);
      
      // Switch to the trip details tab - this should update the existing "new" tab
      onTabChange(createdTripData.tripCode);
      return;
    }
    // Original trip creation handling
    if (createdTripData && createdTripData.tripCode) {
     
    }
  };

  const handleTabClose = (tripId: string) => {
    // Reset reports state when Reports tab is closed
    if (tripId === 'Reports') {
      dispatch(resetReportsState());
    }
    
    // Reset lead conversion report state when LeadsReport tab is closed
    if (tripId === 'LeadsReport') {
      dispatch(resetLeadConversionReport());
    }
    
    // Call the original onCloseTab function
    onCloseTab(tripId);
  };

  return (
    <MainContentWrapper>
      {/* Navigation Loading Overlay */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: 'rgba(0, 0, 0, 0.7)' 
        }}
        open={isNavigating}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={40} />
          <Typography variant="body1">Loading trip details...</Typography>
        </Box>
      </Backdrop>

      <Box>
        <StyledTabs 
          value={activeTab} 
          onChange={(_: React.SyntheticEvent, newValue: string) => {
            // Prevent tab changes during navigation
            if (!isNavigating) {
              onTabChange(newValue);
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {openTrips.map((trip, index) => {
            const tripId = trip.tripId || trip.trip_id || '';
            const isFlashDeal = trip.tripType === 'FLASH_DEALS';
            const isTripNotifications = trip.tripType === 'TRIP_NOTIFICATIONS';
            const tripLabel = (isFlashDeal && tripId !== 'FlashDeals')
                ? `fl-${trip.tripId || trip.trip_id || 'Unknown Trip'}` 
                : trip.tripId || trip.trip_id || 'Unknown Trip';           
            return (
              <StyledTab
                key={`${tripId}-${index}`} // Force re-render with index
                value={tripId}
                disabled={isNavigating} // Disable tabs during navigation
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isTripNotifications ? (
                      <Badge
                        badgeContent={notifications.length}
                        color="error"
                        sx={{
                          '& .MuiBadge-badge': {
                            right: -7,
                            top: -3,
                          }
                        }}
                      >
                        <span>{tripLabel}</span>
                      </Badge>
                    ) : (
                      <span>{tripLabel}</span>
                    )}
                    <IconButton
                      size="small"
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        if (!isNavigating) {
                          handleTabClose(tripId); // Use the new handler
                        }
                      }}
                      sx={{ color: 'inherit' }}
                      disabled={isNavigating}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
              />
            );
          })}
        </StyledTabs>
      </Box>
      {activeTab && (
        <TabPanel sx={activeTab === 'Scheduler' ? { padding: 0 } : undefined}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : activeTab === 'pricing' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h5" component="h2" sx={{ p: 2, pb: 1 }}>
                Pricing Guidelines
              </Typography>
              <Box sx={{ flexGrow: 1, overflow: 'auto', position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                  <PricingTable />
                </Box>
              </Box>
            </Box>
          ) : activeTab === 'Reports' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Reports onTripClick={onAddTrip} onTabChange={onTabChange} />
            </Box>
          ) : activeTab === 'LeadsReport' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <LeadConversionReport />
            </Box>
         ) :  (activeTab === 'FlashDeals' || openTrips.find(trip => (trip.tripId || trip.trip_id) === activeTab)?.tripType === 'FLASH_DEALS') ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <FlashDeals 
                onTripClick={onAddTrip} 
                onTabChange={onTabChange} 
                onCloseTab={onCloseTab}
                mode={openTrips.find(trip => (trip.tripId || trip.trip_id) === activeTab)?.mode || 'create'}
                flashDealData={openTrips.find(trip => (trip.tripId || trip.trip_id) === activeTab)} 
              />
            </Box>
          ) : activeTab === 'CashReports' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CashTransactionReport onTripClick={onAddTrip} onTabChange={onTabChange} />
            </Box>
          ) : activeTab === 'TripNotifications' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <TripNotifications onTripClick={onAddTrip} onTabChange={onTabChange} />
            </Box>
          ): activeTab === 'CashManagement' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CashTransactions onTripClick={onAddTrip} onTabChange={onTabChange} />
            </Box>
          )  : activeTab === 'callback' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CallbackRequestPanel />
            </Box>
          ) : activeTab === 'contact' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <ContactRequestPanel />
            </Box>
          ) : activeTab === 'Scheduler' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <VehicleScheduler/>
            </Box>) : activeTab === 'new' ? (
            <Box sx={{ p: 3 }}>
              <NewTripForm 
                onClose={handleNewTripFormClose} 
                onTripCreated={handleTripCreated} 
              />
            </Box>
          ) : tripDetail || activeTab ? (
            <>
              <ServiceSection>
                {tripDetail && (
                  <TripDetailPanelHeader 
                    tripDetail={tripDetail}
                    onAssignToSelf={() =>{} }
                    onAssignOperator={() => {}}
                  />
                )}
                <TripDetailPanel tripCode={activeTab} tripDetail={tripDetail} />
              </ServiceSection>
            </>
          ) : null}
        </TabPanel>
      )}
    </MainContentWrapper>
  );
};

export default MainContent;
